<!--
  - Copyright (c) ProviderTrust LLC.
  - All Rights Reserved.
  -
  - This software is confidential and proprietary information of
  - ProviderTrust LLC ("Confidential Information"). You shall not disclose
  - such Confidential Information and shall use it only in
  - accordance with the terms of the license agreement you entered
  - into with ProviderTrust.
  -->

<template>
  <registration-template>
    <h1 v-if="planType === 'COMPANY'" slot="header-title">
      Enter account and payment info
    </h1>
    <h1 v-else-if="planType === 'INVITATION'" slot="header-title">
      {{ userInvitationInfo.senderUserName }} has invited you to join
      {{ userInvitationInfo.clientInvitedTo }}
    </h1>
    <h1 v-else slot="header-title">Sign up for your trial</h1>

    <h3 v-if="planType === 'COMPANY'" slot="header-description">
      Create your account and submit payment information. Once you have
      completed this step you can invite your teammates and start searching!
    </h3>
    <h3 v-else-if="planType === 'INVITATION'" slot="header-description">
      Complete the form below to sign up for your account and join the
      {{ userInvitationInfo.teamInvitedTo }} team.
    </h3>
    <h3 v-else slot="header-description">
      Complete the form below to sign up and get your ten free searches per
      month.
    </h3>

    <div slot="content">
      <v-alert
        v-if="planType === 'FREE'"
        type="error"
        :value="errorMessage != null"
        class="mb-3"
      >
        {{ errorMessage }}
      </v-alert>

      <v-alert
        type="error"
        :value="remoteErrors['PAYMENT_AUTHENTICATION_REJECTION']"
        class="mb-3 text-sm-left"
      >
        There was a problem processing your credit card. Please double check
        your payment information and try again.

        <ul class="ml-3">
          <li
            v-for="authError in remoteErrors.PAYMENT_AUTHENTICATION_REJECTION"
            :key="authError"
          >
            {{ authError }}
          </li>
        </ul>
      </v-alert>

      <v-form ref="form" class="text-xs-left">
        <section class="form-section">
          <h2 class="font-bold">Contact Information</h2>
          <v-layout class="row wrap">
            <v-flex class="xs12 sm6">
              <v-text-field
                label="First name"
                v-model.trim="userInfo.firstName"
                @keyup.enter.prevent="onSubmit()"
                :rules="[requiredRule]"
                required
              />
            </v-flex>

            <v-flex class="xs12 sm6">
              <v-text-field
                label="Last name"
                v-model.trim="userInfo.lastName"
                @keyup.enter.prevent="onSubmit()"
                :rules="[requiredRule]"
                required
              />
            </v-flex>

            <v-flex class="xs12 sm6">
              <v-text-field
                label="Email address"
                :disabled="planType === 'INVITATION'"
                v-model.trim="userInfo.email"
                @keyup.enter.prevent="onSubmit()"
                :error-messages="remoteErrors['INVALID_EMAIL']"
                :rules="[requiredRule]"
                required
              />
            </v-flex>

            <v-flex class="xs12 sm6">
              <v-text-field
                label="Company"
                :disabled="planType === 'INVITATION'"
                v-model.trim="userInfo.companyName"
                @keyup.enter.prevent="onSubmit()"
                :rules="[requiredRule]"
                required
              />
            </v-flex>

            <v-flex v-if="planType !== 'INVITATION'" class="xs12 sm6">
              <v-select
                :items="companySizes"
                label="Company Size"
                v-model="userInfo.companySize"
                :rules="[requiredRule]"
                required
              />
            </v-flex>
            <v-flex v-if="planType !== 'INVITATION'" class="xs12 sm6">
              <v-select
                :items="industryTypes"
                label="Industry"
                v-model="userInfo.industry"
                :rules="[requiredRule]"
                required
              />
            </v-flex>
          </v-layout>
        </section>

        <section class="form-section">
          <h2 class="font-bold">Account Information</h2>
          <v-layout class="row wrap">
            <v-flex class="xs12 sm6">
              <v-text-field
                name="username"
                label="Username"
                v-model.trim="userInfo.username"
                @keyup.enter.prevent="onSubmit()"
                v-validate="'verifyUsername'"
                data-vv-validate-on="blur"
                :error-messages="errors.collect('username')"
                :rules="[requiredRule]"
                required
              />
            </v-flex>

            <v-flex class="hint xs12 sm6">
              <p>
                This is the username you will use to log in to ExclusionCheck.
              </p>
            </v-flex>

            <v-flex class="xs12 sm6">
              <v-text-field
                label="Password"
                v-model.trim="userInfo.password"
                @keyup.enter.prevent="onSubmit()"
                :append-icon="passwordHidden ? 'visibility' : 'visibility_off'"
                :append-icon-cb="() => (passwordHidden = !passwordHidden)"
                :type="passwordHidden ? 'password' : 'text'"
                :error-messages="
                  remoteErrors['INVALID_PASSWORD'] ||
                    remoteErrors['PASSWORDS_DO_NOT_MATCH']
                "
                :rules="[requiredRule]"
                required
              />
            </v-flex>

            <v-flex class="hint xs12 sm6">
              <p>
                Password must have at least 7 characters, one letter, one
                number, and not based on a dictionary word.
              </p>
            </v-flex>

            <v-flex class="xs12 sm6">
              <v-text-field
                label="Confirm password"
                v-model.trim="userInfo.verifyPassword"
                @keyup.enter.prevent="onSubmit()"
                :append-icon="
                  verifyPasswordHidden ? 'visibility' : 'visibility_off'
                "
                :append-icon-cb="
                  () => (verifyPasswordHidden = !verifyPasswordHidden)
                "
                :type="verifyPasswordHidden ? 'password' : 'text'"
                :error-messages="
                  remoteErrors['INVALID_PASSWORD'] ||
                    remoteErrors['PASSWORDS_DO_NOT_MATCH']
                "
                :rules="[requiredRule]"
                required
              />
            </v-flex>
          </v-layout>
        </section>

        <section v-if="planType === 'COMPANY'" class="form-section">
          <h2 class="font-bold">Payment Information</h2>
          <v-layout class="row wrap">
            <v-flex xs12> <div id="cc-dropin-container" /> </v-flex>
          </v-layout>
        </section>
      </v-form>

      <section v-if="planType === 'COMPANY'" class="form-section text-xs-left">
        <p>
          You will have a chance to review the options you have selected and the
          information you are submitting on the next screen.
        </p>

        <div class="d-flex pt-4">
          <v-spacer class="w-100" />
          <v-btn flat class="flex-none" @click.native="updateCurrentStep(1)">
            Go Back
          </v-btn>

          <v-btn color="primary" @click.native="onSubmit()" class="flex-none">
            Next
          </v-btn>
        </div>
      </section>

      <section v-if="planType !== 'COMPANY'" class="form-section text-xs-left">
        <registration-tos />

        <div class="d-flex pt-4">
          <v-spacer class="w-100" />
          <v-btn
            color="primary"
            class="flex-none"
            @click.native="onSubmit()"
            :loading="creatingUser"
            :disabled="creatingUser"
          >
            Sign Me Up!
          </v-btn>
        </div>
      </section>
    </div>
  </registration-template>
</template>

<script>
import Vue from "vue"
import dropin from "braintree-web-drop-in"
import axios from "axios"
import RegistrationTemplate from "./Registration-Template"
import RegistrationTos from "./Registration-Tos"
import { mapGetters, mapMutations } from "vuex"
import VeeValidate, { Validator } from "vee-validate"
import CreateUser from "@/mixins/createUser"
import { requiredRule } from "@/common/constants"

// VeeValidate is used along with Vuetify's default rules for validation in this module
Vue.use(VeeValidate)

export default {
  name: "RegistrationAccountInfo",

  components: {
    RegistrationTemplate,
    RegistrationTos
  },

  mixins: [CreateUser],

  computed: {
    ...mapGetters(["userInfo", "planType", "errorData", "userInvitationInfo"]),

    remoteErrors: function() {
      if (!this.errorData || !this.errorData.userCreationError) {
        return {}
      }

      let errorCode = this.errorData.userCreationError
      let errorMessages = this.errorData.errorMessages

      if (errorCode === "PAYMENT_AUTHENTICATION_REJECTION") {
        return {
          PAYMENT_AUTHENTICATION_REJECTION: errorMessages
        }
      }

      return {
        [errorCode]:
          (errorMessages.length && errorMessages[0]) ||
          this.defaultErrorMessages[errorCode] ||
          "This field is invalid"
      }
    }
  },

  data() {
    return {
      defaultErrorMessages: Object.freeze({
        PASSWORDS_DO_NOT_MATCH: "Passwords do not match."
      }),
      requiredRule,
      passwordHidden: true,
      verifyPasswordHidden: true,
      dropinInstance: null,
      companySizes: ["0 - 500", "501 - 3,000", "3,001+"],
      industryTypes: [
        "Health Plan",
        "Health System",
        "Pharmacy / Biotech",
        "Other"
      ]
    }
  },

  watch: {
    errorData: function() {
      this.$validator.validateAll()
    }
  },

  created() {
    this.initializeBraintree()

    Validator.extend("verifyUsername", {
      getMessage: () => `This username is already used.`,

      validate: username =>
        axios
          .get(`/services/user/status/${this.userInfo.username}`)
          .then(response => ({ valid: response.data !== "TAKEN" }))
    })
  },

  methods: {
    ...mapMutations([
      "updateLastFourCC",
      "updateErrorData",
      "updateCurrentStep"
    ]),

    // TODO refactor using braintree.js with promise API instead of callback
    initializeBraintree: function() {
      if (this.planType === "COMPANY") {
        axios
          .get("/services/braintree/clienttoken")
          .then(response => this.createBraintreeDropinForm(response.data))
      }
    },

    createBraintreeDropinForm: function(token) {
      document.getElementById("cc-dropin-container").innerHTML = ""

      let self = this
      dropin.create(
        {
          authorization: token,
          container: "#cc-dropin-container"
        },
        function(createErr, instance) {
          if (createErr) {
            console.error(createErr)
            return
          }
          self.dropinInstance = instance
        }
      )
    },

    onSubmit() {
      if (!this.$refs.form.validate() || this.errors.any()) {
        return
      }

      if (this.planType === "COMPANY") {
        this.validateAndContinue()
      } else {
        this.createUser()
      }
    },

    validateAndContinue() {
      this.updateErrorData(null)
      axios.post(`/services/user/validate`, this.userInfo).then(
        () => {
          this.dropinInstance.requestPaymentMethod((err, payload) => {
            if (err) {
              console.error(err)
              return
            }

            if (payload && payload.nonce) {
              this.userInfo.nonce = payload.nonce
              this.updateCurrentStep(3)
            }
            if (payload && payload.details && payload.details.lastFour) {
              this.updateLastFourCC(payload.details.lastFour)
            }
          })
        },
        error => {
          this.updateErrorData(error.response.data)
          window.scrollTo(0, 0)
        }
      )
    }
  }
}
</script>

<style lang="scss" scoped>
.layout > .flex {
  padding: 5px;
}

.hint {
  margin: auto;
}

.form-section {
  margin-top: 1rem;
  margin-bottom: 1rem;
}
</style>

<style lang="scss">
$error-color: #ff5252;

[data-braintree-id="wrapper"] .braintree-dropin {
  font-family: $font-family;

  .braintree-form__field-error {
    color: $error-color;
  }
  .braintree-form__field-group--has-error .braintree-form__hosted-field {
    border-color: $error-color !important;
  }
  .braintree-form__field-error-icon use {
    fill: $error-color;
  }
  .braintree-sheet--has-error {
    .braintree-sheet {
      border-color: $error-color;
    }
    .braintree-sheet__error {
      border-color: $error-color;
      background-color: $error-color;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Registration-Account-Info.vue